import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import type { MouseEvent } from 'react';

import { CharacterCounter } from '../features/character-limit/CharacterCounter';
import { ClearInputButton } from '../features/clear-input/ClearInputButton';
import { LanguageButtons } from '../features/multi-language/LanguageButtons';
import { contentClasses, contentPaddingY, lineBoxClasses, placeholderClasses } from '../ui/styles';
import { useEditorProps, useEditorSlot } from './context/EditorPropsContext';
import { useFeatureEnabled } from './context/FeaturesContext';

export type ContentAreaIds = { label?: string; error?: string };

/**
 * The editable text, with the `leading` / `trailing` slots and the trailing controls beside it.
 * - Matches Froala's `.fr-view`: padded, auto height, word-breaking; the placeholder is overlaid.
 * - A flex row: the slots centre on the first line; the counter, language pills and clear X
 *   centre on one line and sit at the bottom on more.
 */
export const ContentArea = ({ ids }: { ids: ContentAreaIds }) => {
  const [editor] = useLexicalComposerContext();
  const { mode, placeholder, size = 'm', rows, required, error, errorState } = useEditorProps();
  // Without a toolbar the language pills float here instead
  const hasToolbar = useFeatureEnabled('richText');
  const leading = useEditorSlot('leading');
  const trailing = useEditorSlot('trailing');

  // Like `Input`'s slots: a click beside the text still puts the caret in it. A text field in the slot
  // (e.g. a picker's search) takes its own focus; the editor stays active, as the field is inside it.
  const focusEditor = (e: MouseEvent) => {
    if (e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable]')) return;
    e.preventDefault();
    editor.focus();
  };
  const slotClasses = `flex shrink-0 items-center self-start empty:hidden ${lineBoxClasses[size]}`;

  const contentEditable = (
    <div className="relative flex">
      <div className={`${slotClasses} pl-2`} onMouseDown={focusEditor}>
        {leading()}
      </div>
      <div className="relative min-w-0 flex-1">
        <ContentEditable
          aria-placeholder={placeholder ?? ''}
          aria-labelledby={ids.label}
          aria-describedby={ids.error}
          aria-invalid={errorState || !!error}
          aria-required={required}
          className={`${contentClasses[size]} min-h-auto font-sans wrap-break-word text-black outline-none`}
          // `lh` is the content's own line height, so `rows` lines plus the padding
          style={rows ? { minHeight: `calc(${rows}lh + ${contentPaddingY[size]})` } : undefined}
          placeholder={<span className={`text-text-2/50 pointer-events-none absolute select-none ${placeholderClasses[size]}`}>{placeholder}</span>}
        />
      </div>
      <div className={slotClasses} onMouseDown={focusEditor}>
        {trailing()}
      </div>
      <CharacterCounter />
      {!hasToolbar && <LanguageButtons className={`self-end pr-2 ${lineBoxClasses[size]}`} />}
      <ClearInputButton />
    </div>
  );

  // The one place the mode picks Lexical's editing behaviour; everything else asks the registry
  return mode === 'rich' ? (
    <RichTextPlugin contentEditable={contentEditable} ErrorBoundary={LexicalErrorBoundary} />
  ) : (
    <PlainTextPlugin contentEditable={contentEditable} ErrorBoundary={LexicalErrorBoundary} />
  );
};
